// Product picker for the invoice editor: search the catalogue, set quantity and
// price per row, add to the draft. Quantity and price are editable here because
// an invoice line often differs from the catalogue price.

import { useState } from 'react';
import { api } from '../lib/api';
import { useAsync, useDebounced } from '../lib/hooks';
import { useT } from '../lib/i18n';
import { useSession } from '../lib/session';
import { Icon } from './Icon';
import { EmptyState, ErrorAlert, LoadingRow, Pagination, SearchInput } from './ui';
import type { Product } from '@shared/types';

const PER_PAGE = 8;

export interface PickedLine {
  productId: number;
  code: string;
  description: string;
  quantity: number;
  unitPrice: number;
}

export function ProductPicker({ onAdd }: { onAdd: (line: PickedLine) => void }) {
  const t = useT();
  const { money } = useSession();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  // Per-row overrides, keyed by product id, reset when the product is added.
  const [drafts, setDrafts] = useState<Record<number, { quantity: string; price: string }>>({});

  const q = useDebounced(search);
  const { data, loading, error, reload } = useAsync(
    () => api.products({ q, page, perPage: PER_PAGE, activeOnly: true }),
    [q, page]);

  const draftFor = (product: Product) =>
    drafts[product.id] ?? { quantity: '1', price: String(product.unitPrice) };

  // Seeded from the product, not from blanks: editing the quantity of a row
  // that has no draft yet must not wipe its price (and vice versa).
  const setDraft = (product: Product, patch: Partial<{ quantity: string; price: string }>) =>
    setDrafts((prev) => ({
      ...prev,
      [product.id]: {
        ...(prev[product.id] ?? { quantity: '1', price: String(product.unitPrice) }),
        ...patch,
      },
    }));

  function add(product: Product) {
    const draft = draftFor(product);
    const quantity = Number(draft.quantity.replace(',', '.'));
    const unitPrice = Number(draft.price.replace(',', '.'));
    if (!Number.isFinite(quantity) || quantity <= 0) return;
    if (!Number.isFinite(unitPrice) || unitPrice < 0) return;
    onAdd({
      productId: product.id,
      code: product.code,
      description: product.name,
      quantity,
      unitPrice,
    });
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[product.id];
      return next;
    });
  }

  return (
    <div className="stack">
      <SearchInput value={search} onChange={(v) => { setSearch(v); setPage(1); }}
        placeholder={t('invoice.picker.searchPlaceholder')} />

      {error ? <ErrorAlert message={error} onRetry={reload} />
        : loading && !data ? <LoadingRow />
        : data && data.items.length === 0 ? (
          <EmptyState icon="search" title={t('common.noResults')} hint={t('common.noResultsHint')} />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th className="shrink">{t('products.field.code')}</th>
                  <th>{t('products.field.name')}</th>
                  <th className="shrink num">{t('invoice.lines.quantity')}</th>
                  <th className="shrink num">{t('invoice.lines.unitPrice')}</th>
                  <th className="shrink" />
                </tr>
              </thead>
              <tbody>
                {data?.items.map((product) => {
                  const draft = draftFor(product);
                  return (
                    <tr key={product.id}>
                      <td className="shrink mono">{product.code}</td>
                      <td><span className="table__primary">{product.name}</span></td>
                      <td className="shrink">
                        <input className="input input--numeric line-qty" inputMode="decimal"
                          aria-label={t('invoice.lines.quantity')}
                          value={draft.quantity}
                          onChange={(e) => setDraft(product, { quantity: e.target.value })}
                          onKeyDown={(e) => { if (e.key === 'Enter') add(product); }} />
                      </td>
                      <td className="shrink">
                        <input className="input input--numeric line-price" inputMode="decimal"
                          aria-label={`${t('invoice.lines.unitPrice')} (${money.symbol})`}
                          value={draft.price}
                          onChange={(e) => setDraft(product, { price: e.target.value })}
                          onKeyDown={(e) => { if (e.key === 'Enter') add(product); }} />
                      </td>
                      <td className="shrink">
                        <button type="button" className="btn btn--primary btn--sm" onClick={() => add(product)}>
                          <Icon name="plus" size={14} />
                          {t('invoice.picker.add')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

      {data && data.items.length > 0 && (
        <div className="row row--between">
          <Pagination page={data.page} totalPages={data.totalPages} total={data.total}
            perPage={data.perPage} onChange={setPage} />
        </div>
      )}
    </div>
  );
}
